class AccountDrawer extends HTMLElement {
  constructor() {
    super();

    this.externallyOpenedTier = null;
  }

  get selectors() {
    return {
      drawer: {
        button: {
          open: "[data-account-drawer-open]",
          close: "[data-account-drawer-close]"
        },
        scrollableContent: "[data-account-drawer-scrollable]"
      },
      tieredMenu: {
        container: "[data-account-drawer-tier]",
        trigger: "[data-account-drawer-tier-trigger]",
        backButton: "[data-account-drawer-tier-back-button]"
      }
    }
  }

  get modifiers() {
    return {
      drawer: {
        open: "drawer--open"
      },
      tieredMenu: {
        open: "account-drawer__navigation-tier--open",
        noTransition: "account-drawer__navigation-tier--no-transition"
      },
      button: {
        open: {
          pulse: "header__circle-icon--pulse"
        }
      }
    }
  }

  connectedCallback() {
    this.initTieredMenuListeners();
    this.initDrawerListeners();

    window.addEventListener("resize", this.setDrawerHeight.debounce(50).bind(this));

    if (!isThemeDesignMode()) {
      this.setAttribute("inert", "");
      this.querySelectorAll(this.selectors.tieredMenu.container).forEach((menu) => {
        menu.setAttribute("inert", "");
      });
    }
  }

  disconnectedCallback() {
    this.removeTrapFocus();
    this.restoreBackground();
    this.destroyTieredMenuListeners();
    this.destroyDrawerListeners();

    window.removeEventListener("resize", this.setDrawerHeight.debounce(50).bind(this));
  }

  initDrawerListeners() {
    document.documentElement.customDelegate(
      "click",
      this.selectors.drawer.button.open,
      this.openDrawer.bind(this)
    );

    document.documentElement.customDelegate(
      "click",
      this.selectors.drawer.button.close,
      this.closeDrawer.bind(this)
    );
  }

  destroyDrawerListeners() {
    document.documentElement.removeCustomDelegate(
      "click",
      this.selectors.drawer.button.open,
      this.openDrawer.bind(this)
    );

    document.documentElement.removeCustomDelegate(
      "click",
      this.selectors.drawer.button.close,
      this.closeDrawer.bind(this)
    );
  }

  openDrawer(event) {
    if (event) {
      event.preventDefault();
    }

    const opener = document.activeElement;
    const scrollableContent = this.querySelector(this.selectors.drawer.scrollableContent);

    bodyScrollLock.disableBodyScroll(scrollableContent);

    this.removeAttribute("inert");
    this.classList.add(this.modifiers.drawer.open);
    this.querySelectorAll(this.selectors.tieredMenu.container).forEach((menu) => {
      if (!menu.classList.contains(this.modifiers.tieredMenu.open)) {
        menu.setAttribute("inert", "");
      }
    });
    this.inertBackground();
    this.trapFocus();
    this.focusDrawer(opener);

    document.querySelector(this.selectors.drawer.button.open)?.classList.remove(this.modifiers.button.open.pulse);
  }

  closeDrawer(event) {
    if (event) {
      event.preventDefault();
    }

    const scrollableContent = this.querySelector(this.selectors.drawer.scrollableContent);

    this.classList.remove(this.modifiers.drawer.open);
    this.closeAllTieredMenus();
    this.removeTrapFocus();
    this.releaseDrawerBehindTier();
    this.restoreBackground();

    if (!isThemeDesignMode()) {
      this.setAttribute("inert", "");
    }

    bodyScrollLock.enableBodyScroll(scrollableContent);

    this.returnFocusToPreviousElement();

    this.externallyOpenedTier = null;
  }

  setDrawerHeight() {
    const drawerHeight = window.innerHeight;

    this.style.setProperty(
      '--drawer-height',
      `${drawerHeight}px`
    );
  }

  initTieredMenuListeners() {
    this._onTierTriggerKeydown = (event) => {
      if (event.key === "Enter" || event.key === " ") {
        const trigger = event?.target?.closest(this.selectors.tieredMenu.trigger);

        if (!trigger) {
          return false;
        }

        event.preventDefault();

        const menuId = trigger.getAttribute("data-account-drawer-tier-trigger");

        this.openTieredMenu(menuId);
      }
    };

    this._onTierTriggerClick = (event) => {
      const trigger = event?.target?.closest(this.selectors.tieredMenu.trigger);

      if (!trigger) {
        return;
      }

      event.preventDefault();

      const menuId = trigger.getAttribute("data-account-drawer-tier-trigger");

      this.openTieredMenu(menuId);
    };

    this._onTierBackButtonClick = (event) => {
      const backButton = event?.target?.closest(this.selectors.tieredMenu.backButton);

      if (!backButton) {
        return false;
      }

      event.preventDefault();

      this.closeTieredMenu(backButton);
    };

    document.addEventListener("keydown", this._onTierTriggerKeydown);
    document.addEventListener("click", this._onTierTriggerClick);
    document.addEventListener("click", this._onTierBackButtonClick);
  }

  destroyTieredMenuListeners() {
    document.removeEventListener("keydown", this._onTierTriggerKeydown);
    document.removeEventListener("click", this._onTierTriggerClick);
    document.removeEventListener("click", this._onTierBackButtonClick);
  }

  openTieredMenu(menuId, { immediate = false } = {}) {
    if (!menuId) {
      return;
    }

    const menu = document.querySelector(`[data-account-drawer-tier='${menuId}']`); // TODO

    if (!menu) {
      return;
    }

    if (immediate) {
      menu.classList.add(this.modifiers.tieredMenu.noTransition);
    }

    bodyScrollLock.disableBodyScroll(menu);

    menu.classList.add(this.modifiers.tieredMenu.open);
    menu.removeAttribute("inert");
    this.inertDrawerBehindTier();
    this.removeTrapFocus();

    const focusMenu = () => {
      const focusableElements = menu.getFocusableElements();
      const firstFocusableElement = focusableElements[0];

      if (firstFocusableElement) {
        firstFocusableElement.focus();
      }

      menu.trapFocus();

      if (immediate) {
        menu.classList.remove(this.modifiers.tieredMenu.noTransition);
      }
    };

    if (immediate) {
      requestAnimationFrame(focusMenu);
    } else {
      setTimeout(focusMenu, 400);
    }
  }

  closeTieredMenu(backButton) {
    if (!backButton) {
      return;
    }

    const menu = backButton.closest(this.selectors.tieredMenu.container); // TODO

    if (!menu) {
      return;
    }

    const menuId = menu.getAttribute("data-account-drawer-tier");
    const trigger = this.querySelector(`[data-account-drawer-tier-trigger="${menuId}"]`);

    menu.removeTrapFocus();
    menu.classList.remove(this.modifiers.tieredMenu.open);
    menu.setAttribute("aria-expanded", "false");
    menu.setAttribute("inert", "");
    this.releaseDrawerBehindTier();

    bodyScrollLock.enableBodyScroll(menu);

    if (this.externallyOpenedTier) {
      this.closeDrawer();
      return;
    }

    if (this.classList.contains(this.modifiers.drawer.open)) {
      this.trapFocus();

      if (trigger) {
        trigger.focus();
      } else {
        this.focus({ preventScroll: true });
      }
    }
  }

  closeAllTieredMenus() {
    const tieredMenus = [
      ...this.querySelectorAll(this.selectors.tieredMenu.container)
    ];

    if (!tieredMenus.length) {
      return false;
    }

    tieredMenus.forEach((tieredMenu) => {
      tieredMenu.removeTrapFocus();
      tieredMenu.classList.remove(this.modifiers.tieredMenu.open);
      tieredMenu.setAttribute("aria-expanded", "false");
      tieredMenu.setAttribute("inert", "");

      bodyScrollLock.enableBodyScroll(tieredMenu);
    });
  }

  inertDrawerBehindTier() {
    this._drawerBehindTier = [
      this.querySelector(".drawer__container > .drawer__wrapper > .account-drawer__section--header"),
      this.querySelector("[data-account-drawer-scrollable]")
    ].filter(Boolean);

    this._drawerBehindTier.forEach((element) => {
      if (element.hasAttribute("inert")) return;

      element.setAttribute("inert", "");
      element.setAttribute("data-tier-inert", "");
    });
  }

  releaseDrawerBehindTier() {
    (this._drawerBehindTier || []).forEach((element) => {
      if (!element.hasAttribute("data-tier-inert")) return;

      element.removeAttribute("inert");
      element.removeAttribute("data-tier-inert");
    });

    this._drawerBehindTier = [];
  }

  openDrawerWithTier(menuId) {
    this.externallyOpenedTier = menuId;

    this.openDrawer();
    this.openTieredMenu(menuId, { immediate: true });
  }

  focusDrawer(opener) {
    if (opener && !this.contains(opener)) {
      this.previouslyFocusedElement = opener;
    }

    this.focus({
      preventScroll: true
    });
  }

  returnFocusToPreviousElement() {
    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus();
    }
  }
}

if (!customElements.get("account-drawer")) {
  customElements.define("account-drawer", AccountDrawer);
}